import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, Home } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Formulaire public « Je suis intéressé » (V13).
 *
 * Crée UNIQUEMENT un prospect via POST /api/prospects : aucune réservation, aucun choix de lot ni
 * d'unité, aucun client, contrat ou paiement. Un conseiller TPCO rappelle le prospect ; le contrat
 * (typologie, numéro de lot réel, prix, échéancier) est établi ensuite par l'administration.
 */
function fmtXOF(n) {
  return n ? Number(n).toLocaleString("fr-FR") + " FCFA" : null;
}

const vide = { typologie_souhaitee: "", prenom: "", nom: "", telephone: "", email: "", message: "",
               budget: "", apport_envisage: "", duree_envisagee: "", honeypot: "" };

export default function Souscription() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [typologies, setTypologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(vide);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/souscription/project/${slug}`);
        setProject(data.projet);
        setTypologies(data.typologies || []);
      } catch (e) {
        setErr(e.response?.data?.detail || "Projet introuvable");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const entierOuNull = (v) => (v === "" || v === null ? null : Math.max(0, Math.round(Number(v))));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.prenom.trim() || !form.nom.trim() || !form.telephone.trim()) {
      setErr("Prénom, nom et téléphone sont obligatoires.");
      return;
    }
    setSending(true);
    try {
      const payload = {
        programme_id: project?.id, prenom: form.prenom.trim(), nom: form.nom.trim(), telephone: form.telephone.trim(),
        email: form.email.trim() || null, message: form.message.trim(), honeypot: form.honeypot,
        typologie_souhaitee: form.typologie_souhaitee || null, budget: entierOuNull(form.budget),
        apport_envisage: entierOuNull(form.apport_envisage), duree_envisagee: entierOuNull(form.duree_envisagee),
      };
      const { data } = await axios.post(`${API}/api/prospects`, payload);
      setDone(data);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Merci de vérifier les champs saisis.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><Loader2 className="w-10 h-10 animate-spin text-[#E91E63]" /></div>;
  if (!project) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] text-[#4B5775]"><div className="text-center"><div className="font-bold text-xl mb-2">Projet introuvable</div><div>{err}</div></div></div>;

  if (done) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6" data-testid="interet-success">
        <div className="bg-white rounded-2xl shadow-lg border border-[#E2E5F1] p-8 max-w-lg text-center">
          <div className="w-14 h-14 rounded-full bg-[#2D7A3F] text-white flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7" /></div>
          <h1 className="text-2xl font-bold text-[#0F1A3D] mb-2">Merci pour votre intérêt !</h1>
          <p className="text-[#4B5775] text-sm">{done.message}</p>
          <p className="text-[#9CA3B5] text-xs mt-4">Aucun lot n'est réservé à ce stade : votre conseiller vous présentera les disponibilités.</p>
          <Link to="/" className="inline-block mt-6 text-sm font-bold text-[#E91E63]">← Retour au site</Link>
        </div>
      </div>
    );
  }

  const input = "w-full h-11 px-3 rounded-lg border border-[#E2E5F1] text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E63]/30";
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="interet-form-page">
      <div className="bg-[#0F1A3D] text-white py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-white/70 hover:text-white text-xs uppercase tracking-wider">← Retour au site</Link>
          <div className="text-lg font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{project.nom}</div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto p-6 md:p-10">
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg border border-[#E2E5F1] p-6 md:p-10 space-y-5" data-testid="interet-form">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-[#E91E63]">{project.nom}</div>
            <h1 className="text-2xl font-bold text-[#0F1A3D] mt-1">Je suis intéressé(e)</h1>
            <p className="text-sm text-[#4B5775] mt-1">Laissez vos coordonnées : un conseiller TPCO vous rappelle pour vous présenter le programme.</p>
          </div>
          <input type="text" value={form.honeypot} onChange={(e) => set("honeypot", e.target.value)} tabIndex={-1} autoComplete="off"
                 style={{ position: "absolute", left: "-9999px", opacity: 0 }} aria-hidden="true" />

          {typologies.length > 0 && (
            <div>
              <div className="text-xs font-bold text-[#0F1A3D] mb-2">Typologie souhaitée</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {typologies.map((t) => (
                  <button type="button" key={t.code} onClick={() => set("typologie_souhaitee", form.typologie_souhaitee === t.code ? "" : t.code)}
                          className={`p-3 rounded-lg border text-left transition ${form.typologie_souhaitee === t.code ? "border-[#E91E63] bg-[#FDECF2]" : "border-[#E2E5F1] hover:border-[#C5CCE8]"}`}
                          data-testid={`typo-${t.code}`}>
                    <div className="flex items-center gap-2 font-bold text-[#0F1A3D]"><Home className="w-4 h-4" /> {t.nom || t.code}</div>
                    <div className="text-[11px] text-[#4B5775] mt-1">
                      {t.surface_m2 ? `${t.surface_m2} m² · ` : ""}{fmtXOF(t.prix_vente) || "Prix sur demande"}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input className={input} placeholder="Prénom *" value={form.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="in-prenom" />
            <input className={input} placeholder="Nom *" value={form.nom} onChange={(e) => set("nom", e.target.value)} data-testid="in-nom" />
            <input className={input} placeholder="Téléphone *" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} data-testid="in-telephone" />
            <input className={input} type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="in-email" />
          </div>
          <textarea className={`${input} h-24 py-2`} placeholder="Votre message (questions, disponibilités…)" value={form.message}
                    onChange={(e) => set("message", e.target.value)} data-testid="in-message" />

          <details className="text-sm">
            <summary className="cursor-pointer text-[#1E3A8A] font-bold text-xs">Précisez votre projet de financement (facultatif)</summary>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <input className={input} type="number" min="0" step="1" placeholder="Budget (FCFA)" value={form.budget} onChange={(e) => set("budget", e.target.value)} />
              <input className={input} type="number" min="0" step="1" placeholder="Apport envisagé (FCFA)" value={form.apport_envisage} onChange={(e) => set("apport_envisage", e.target.value)} />
              <input className={input} type="number" min="1" max="30" step="1" placeholder="Durée (années)" value={form.duree_envisagee} onChange={(e) => set("duree_envisagee", e.target.value)} />
            </div>
          </details>

          {err && <div className="p-3 bg-[#FBEAE9] border border-[#F4CFCE] text-[#C83B3B] text-sm rounded-md" data-testid="interet-error">{err}</div>}
          <button type="submit" disabled={sending}
                  className="w-full h-12 rounded-lg bg-[#E91E63] hover:bg-[#D81B60] text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="interet-submit">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <>Être recontacté <Check className="w-4 h-4" /></>}
          </button>
          <p className="text-[11px] text-[#9CA3B5] text-center">Cette demande ne réserve aucun lot et ne vous engage à rien.</p>
        </form>
      </div>
    </div>
  );
}
